import React from "react";
import ReactDOM from "react-dom/client";
import ButtonComponent from "./VoiceManager";
import {fetchEnvironmentSettings} from "./common/services/EnvironmentService";
import {suggest, resolveEmailSha256, resolveRecoUid, getRecommendationProducts} from "./common/services";

export * from "./VoiceSearch";
export * from "./ProductSearch";
export * from "./AudioVisualizer";
export * from "./LoadingIndicator";
export * from "./common/services";
export * from "./VoiceManager";

(function () {
    if (typeof window !== "undefined") {

        const COOKIE_NAME = 'reco_uid';
        const TWO_YEARS = 60 * 60 * 24 * 730;

        function getCookie(name) {
            try {
                return document.cookie
                    .split(';')
                    .map(s => s.trim())
                    .find(s => s.startsWith(name + '='))
                    ?.split('=')[1] || null;
            } catch (_) {
                return null;
            }
        }

        function setCookieRaw(name, val, attrs) {
            const parts = [
                `${name}=${val}`,
                `Path=/`,
            ];
            if (attrs.domain) parts.push(`Domain=${attrs.domain}`);
            if (typeof attrs.maxAge === 'number') parts.push(`Max-Age=${attrs.maxAge}`);
            if (attrs.sameSite) parts.push(`SameSite=${attrs.sameSite}`);
            if (attrs.secure) parts.push(`Secure`);
            document.cookie = parts.join('; ');
        }

        function setCookieSmart(name, val, maxAgeSec) {
            try {
                setCookieRaw(name, val, {domain: '.recomdo.ai', maxAge: maxAgeSec, sameSite: 'None', secure: true});
            } catch (_) {
            }
            let ok = getCookie(name) === val;
            if (!ok) {
                try {
                    setCookieRaw(name, val, {maxAge: maxAgeSec, sameSite: 'None', secure: true});
                } catch (_) {
                }
            }
        }

        function uuid() {
            try {
                if (window.crypto && typeof window.crypto.randomUUID === 'function') {
                    return window.crypto.randomUUID();
                }
            } catch (_) {
            }
            return (Date.now().toString(36) + Math.random().toString(36).slice(2));
        }

        function ensureUid() {
            let v = getCookie(COOKIE_NAME);
            if (!v) {
                v = uuid();
                setCookieSmart(COOKIE_NAME, v, TWO_YEARS);
            }
            return v;
        }

        const __RECOMDO_UID__ = ensureUid();
        try {
            window.__RECOMDO_UID__ = __RECOMDO_UID__;
            sessionStorage.setItem('RECOMDO_UID', __RECOMDO_UID__);
            localStorage.setItem('RECOMDO_UID', __RECOMDO_UID__);
        } catch (_) {
        }

        if (!localStorage.getItem("recodemo") || localStorage.getItem("recodemo") == 'null') {
            const params = new URLSearchParams(window.location.search);
            const isDemo = params.get('recodemo');
            if (isDemo) {
                localStorage.setItem('recodemo', isDemo);
            }
        }

        function setResponsiveIframeWidth(iframe) {
            const width = window.innerWidth;
            let iframeWidth;

            if (width < 640) {
                iframeWidth = 420;
            } else if (width < 768) {
                iframeWidth = 450;
            } else if (width < 1024) {
                iframeWidth = 500;
            } else if (width < 1280) {
                iframeWidth = 550;
            } else {
                iframeWidth = 600;
            }

            iframe.style.width = iframeWidth + "px";
        }

        function setMinimizedIframeSize(iframe) {
            const minimizedWidth = window.innerWidth < 640 ? 200 : 240;
            iframe.style.width = minimizedWidth + "px";
            iframe.style.height = "72px";
            iframe.style.zIndex = window.__VOICE_SEARCH_ENV_SETTINGS__.plugin_platform === 'shopify' ? "999" : "9999";
        }

        const initializeWidget = () => {
            if (!document.body) {
                console.error("❌ document.body is not available.");
                return;
            }

            const iframe = document.createElement("iframe");
            iframe.style.transition = "height 0.35s ease";
            iframe.style.height = "auto";

            setResponsiveIframeWidth(iframe);
            iframe.style.position = "fixed";
            iframe.style.bottom = "0";
            iframe.style.right = "0";
            iframe.style.border = "none";
            iframe.style.zIndex = window.__VOICE_SEARCH_ENV_SETTINGS__.plugin_platform === 'shopify' ? "999" : "9999";
            iframe.style.backgroundColor = "transparent";
            iframe.className = "recomdo_ai";
            iframe.allowTransparency = "true";

            document.body.appendChild(iframe);
            let CLOSED_HEIGHT_PX = null;

            requestAnimationFrame(() => {
                CLOSED_HEIGHT_PX = Math.round(iframe.getBoundingClientRect().height) || 64; // fallback
                iframe.style.height = iframe.style.height || "auto";
            });

            window.addEventListener("resize", () => {
                if (iframe.getAttribute("data-modal-open")) return;
                if (iframe.getAttribute("data-widget-minimized")) {
                    setMinimizedIframeSize(iframe);
                    return;
                }
                setResponsiveIframeWidth(iframe);
            });

            const iframeDocument = iframe.contentDocument || iframe.contentWindow.document;

            if (!iframeDocument) {
                console.error("❌ Cannot access iframe document.");
                return;
            }

            iframeDocument.open();
            iframeDocument.write(`
            <!DOCTYPE html>
            <html>
                <head></head>
                <body></body>
            </html>
        `);
            iframeDocument.close();

            const iframeRoot = iframeDocument.createElement("div");
            iframeRoot.id = "recomdo-app-root";
            iframeDocument.body.appendChild(iframeRoot);

            const ModifiedButtonComponent = () => {
                const OriginalButtonComponent = ButtonComponent;
                return React.createElement(OriginalButtonComponent, {
                    onModalOpen: () =>
                        window.parent.postMessage("openModal", "*"),
                    onModalClose: () =>
                        window.parent.postMessage("closeModal", "*"),
                    onWidgetMinimize: () =>
                        window.parent.postMessage("minimizeWidget", "*"),
                    onWidgetRestore: () =>
                        window.parent.postMessage("restoreWidget", "*")
                });
            };

            const renderApp = () => {
                try {
                    ReactDOM.createRoot(iframeRoot).render(
                        <ModifiedButtonComponent/>
                    );
                } catch (e) {
                    console.error("❌ React render failed", e);
                }
            };

            const tailwindScript = iframeDocument.createElement("script");
            tailwindScript.src = "https://cdn.tailwindcss.com";
            tailwindScript.async = true;

            tailwindScript.onload = () => {
                renderApp();
            };

            tailwindScript.onerror = (e) => {
                console.warn(
                    "Tailwind failed to load. Rendering without it.",
                    e
                );
                renderApp();
            };

            iframeDocument.head.appendChild(tailwindScript);
            const additionalStyles = iframeDocument.createElement("style");

            additionalStyles.textContent = `
                body { 
                    margin: 0; 
                    padding: 0; 
                    overflow: hidden;
                    background-color: transparent !important;
                }

                .modal-open {
                    width: 100%;
                    height: 100vh;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background-color: rgba(0,0,0,0.5);
                }

                .dot-pulse {
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;
                    margin-left: 4px;
                }

                .dot-pulse > span {
                    width: 4px;
                    height: 4px;
                    border-radius: 9999px;
                    background-color: currentColor;
                    display: inline-block;
                    opacity: 0.25;
                    animation: dotPulse 1.2s ease-in-out infinite;
                }

                .dot-pulse > span:nth-child(1) { animation-delay: 0s; }
                .dot-pulse > span:nth-child(2) { animation-delay: 0.2s; }
                .dot-pulse > span:nth-child(3) { animation-delay: 0.4s; }

                @keyframes dotPulse {
                    0%, 100% { opacity: 0.25; transform: translateY(0) scale(1); }
                    50% { opacity: 1; transform: translateY(-2px) scale(1.1); }
                }
            `;

            iframeDocument.head.appendChild(additionalStyles);

            function openModalAnimate() {
                if (!CLOSED_HEIGHT_PX) {
                    CLOSED_HEIGHT_PX = Math.round(iframe.getBoundingClientRect().height) || 64;
                }
                iframe.setAttribute("data-modal-open", "1");
                iframe.removeAttribute("data-widget-minimized");
                iframe.style.transition = "height 0.35s ease";
                const startH = Math.round(iframe.getBoundingClientRect().height);
                iframe.style.height = startH + "px";
                iframe.style.width = "100%";
                iframe.style.left = "0";
                iframe.style.right = "0";
                iframe.style.height = "100vh";
                iframe.style.zIndex =  "9999";

                setTimeout(() => {
                    iframe.style.top = "0";
                    iframe.style.bottom = "auto";
                }, 360);
            }

            function closeModalAnimate() {
                setResponsiveIframeWidth(iframe);
                iframe.style.height = "auto";
                iframe.style.bottom = "0";
                iframe.style.top = "auto";
                iframe.style.left = "auto";
                iframe.style.right = "0";
                iframe.removeAttribute("data-modal-open")
                iframe.style.zIndex = window.__VOICE_SEARCH_ENV_SETTINGS__.plugin_platform === 'shopify' ?  "999" : "9999";
            }

            function minimizeWidgetAnimate() {
                iframe.setAttribute("data-widget-minimized", "1");
                iframe.style.transition = "width 0.25s ease, height 0.25s ease";
                iframe.style.bottom = "0";
                iframe.style.top = "auto";
                iframe.style.left = "auto";
                iframe.style.right = "0";
                setMinimizedIframeSize(iframe);
            }

            function restoreWidgetAnimate() {
                iframe.removeAttribute("data-widget-minimized");
                iframe.style.transition = "width 0.25s ease, height 0.25s ease";
                setResponsiveIframeWidth(iframe);
                iframe.style.height = "auto";
                iframe.style.bottom = "0";
                iframe.style.top = "auto";
                iframe.style.left = "auto";
                iframe.style.right = "0";
                iframe.style.zIndex = window.__VOICE_SEARCH_ENV_SETTINGS__.plugin_platform === 'shopify' ?  "999" : "9999";
            }

            window.addEventListener("message", (event) => {
                if (event.data === "openModal") {
                    openModalAnimate();
                } else if (event.data === "closeModal") {
                    closeModalAnimate();
                } else if (event.data === "minimizeWidget") {
                    minimizeWidgetAnimate();
                } else if (event.data === "restoreWidget") {
                    restoreWidgetAnimate();
                }
            });
        };

        if (!("VoiceSearchWidget" in window)) {
            Object.defineProperty(window, "VoiceSearchWidget", {
                value: async function (config = {}) {
                    window.__VOICE_SEARCH_CONFIG__ = config;
                    if (config.accessKey) {
                        try {
                            const envSettings = await fetchEnvironmentSettings(
                                config.shopUrl,
                                config.accessKey,
                                config.environment || 'production'
                            );
                            if (envSettings) {
                                window.__VOICE_SEARCH_ENV_SETTINGS__ = envSettings;
                                try {
                                    const preloadRecommendations = async () => {
                                        try {
                                            const emailSha = resolveEmailSha256();
                                            const uid = resolveRecoUid();
                                            const suggestData = await suggest(emailSha, uid);
                                            const recItems = Array.isArray(
                                                suggestData?.recommendation_items
                                            ) ? suggestData.recommendation_items : [];
                                            const priceAnchor = suggestData?.profile?.price_anchor ?? null;
                                            if (recItems.length > 0) {
                                                await getRecommendationProducts(
                                                    recItems,
                                                    envSettings.REACT_APP_API_BASE_URL,
                                                    envSettings.REACT_APP_CLIENT_KEY,
                                                    envSettings.REACT_APP_ACCESS_KEY,
                                                    envSettings.REACT_APP_ACCESS_SECRET,
                                                    priceAnchor
                                                );
                                            }
                                        } catch (_) {
                                        }
                                    };
                                    preloadRecommendations();
                                } catch (_) {
                                }
                            } else {
                                console.warn(
                                    'Failed to load environment settings. The widget will not initialize.'
                                );
                                try {
                                    window.__VOICE_SEARCH_UNAUTHORIZED__ = true;
                                } catch (_) {
                                }
                            }

                        } catch (error) {
                            console.error(
                                'Error loading environment settings:',
                                error
                            );
                        }
                    }
                    if (window.__VOICE_SEARCH_UNAUTHORIZED__) {
                        console.error(
                            'Environment bootstrap failed. Voice Search widget will not initialize.'
                        );
                        return;
                    }
                    if (document.readyState === "loading") {
                        window.addEventListener(
                            "DOMContentLoaded",
                            initializeWidget,
                            {once: true}
                        );
                    } else {
                        initializeWidget();
                    }
                },
                writable: false,
                configurable: false,
            });
        }
    }
})();